import { noticeError } from '@ratehub/base-ui';
import { INTENDED_USE } from '@ratehub/documents/mtg-journey-document/v4';
import { fetchBestRates, SCENARIO_TYPES } from '@ratehub/mtg-common';

import { MORTGAGE_TYPES, MORTGAGE_TERMS, BEST_MORTGAGE_SCENARIO_DEFAULT_PARAMS } from '../definitions/MortgageTerms';


/**
 * Fetch function for ProductTableBestMortgageRates
 * @param {object} values   // values from filters
 * @param {string} values.language
 * @param {string} values.scenario
 * @param {object} values.city
 * @param {object} values.helocCity // city specifically for HELOC
 * @param {number} values.homePrice
 * @param {number} values.downPaymentPercent
 * @param {number} values.renewMortgageBalance  // Mortgage balance for renewal
 * @param {number} values.refinanceMortgageBalance  // Mortgage balance for refi
 * @param {number} values.helocAmount
 * @param {number} values.additionalFunds
 * @param {string} values.occupancy
 * @param {boolean} values.isCMHC
 * @param {number} values.renewAmortization // Amortization for renewal
 * @param {number} values.purchaseRefinanceAmortization // Amortization for purchase & refinance
 */
async function fetchProductTableBestMortgageRates(values) {
    const params = getParamsForScenario(values);

    const [ fetchedMarketRates, fetchedBankRates ] = await Promise.all([
        fetchBestMarketRates(params),
        fetchBestBankRates(params),
    ]);

    // lastUpdated could be different between the two fetches,
    //  We want to return the most recent
    const marketRatesDate = new Date(fetchedMarketRates.lastUpdated);
    const banksRatesDate = new Date(fetchedBankRates.lastUpdated);

    return {
        bestMarketRates: formatRates(fetchedMarketRates, params.scenario),
        bestBankRates: formatRates(fetchedBankRates, params.scenario),
        lastUpdatedDate: marketRatesDate >= banksRatesDate
            ? marketRatesDate
            : banksRatesDate,
    };
}

async function fetchBestMarketRates(params) {
    try {
        return await fetchBestRates(params.scenario, params);
    } catch (error) {
        noticeError(error, {
            message: `[ProductTableBestMortgageRates.fetchBestMarketRates] Unable to fetch best market rates: ${error.message}`,
        });

        // Re-throw so Next.js will fail the export of this page.
        throw error;
    }
}

async function fetchBestBankRates(params) {
    try {
        return await fetchBestRates(params.scenario, { ...params, isBigBank: 1 });
    } catch (error) {
        noticeError(error, {
            message: `[ProductTableBestMortgageRates.fetchBestBankRates] Unable to fetch best bank rates ${error.message}`,
        });

        // Re-throw so Next.js will fail the export of this page.
        throw error;
    }
}

function getParamsForScenario(values) {
    const scenario = values.scenario ?? SCENARIO_TYPES.PURCHASE;
    const city = values.scenario === SCENARIO_TYPES.HELOC && values.helocCity
        ? values.helocCity
        : values.city;

    const params = {
        ...BEST_MORTGAGE_SCENARIO_DEFAULT_PARAMS[scenario],

        ...(scenario !== SCENARIO_TYPES.HELOC
            ? {
                isOwnerOccupied: values.occupancy == null
                    ? BEST_MORTGAGE_SCENARIO_DEFAULT_PARAMS[SCENARIO_TYPES.PURCHASE].isOwnerOccupied
                    : (values.occupancy === INTENDED_USE.OWNER_OCCUPIED ? 1 : 0),
            }
            : {}
        ),

        city: city?.name ?? BEST_MORTGAGE_SCENARIO_DEFAULT_PARAMS[scenario].city,
        province: values?.province ?? city?.province?.code ?? BEST_MORTGAGE_SCENARIO_DEFAULT_PARAMS[scenario].province,

        scenario,
        type: Object.values(MORTGAGE_TYPES),
        term: MORTGAGE_TERMS,
        isFeatured: 1,
        language: values.language,
    };

    switch (scenario) {
        case SCENARIO_TYPES.PURCHASE:
            return {
                ...params,
                homePrice: values.homePrice ?? BEST_MORTGAGE_SCENARIO_DEFAULT_PARAMS[SCENARIO_TYPES.PURCHASE].homePrice,
                downPaymentPercent:
                    values.downPaymentPercent
                        ? values.downPaymentPercent / 100
                        : BEST_MORTGAGE_SCENARIO_DEFAULT_PARAMS[SCENARIO_TYPES.PURCHASE].downPaymentPercent,
                amortization: parseInt(
                    values.purchaseRefinanceAmortization
                    ?? BEST_MORTGAGE_SCENARIO_DEFAULT_PARAMS[SCENARIO_TYPES.PURCHASE].amortization,
                ),
            };
        case SCENARIO_TYPES.RENEWAL:
            return {
                ...params,
                amortization: parseInt(
                    values.renewAmortization
                    ?? BEST_MORTGAGE_SCENARIO_DEFAULT_PARAMS[SCENARIO_TYPES.RENEWAL].amortization,
                ),
                isInsured: values.isCMHC == null
                    ? BEST_MORTGAGE_SCENARIO_DEFAULT_PARAMS[SCENARIO_TYPES.RENEWAL].isInsured
                    : (values.isCMHC ? 1 : 0),
            };
        case SCENARIO_TYPES.REFINANCE:
            return {
                ...params,
                amortization: parseInt(
                    values.purchaseRefinanceAmortization
                    ?? BEST_MORTGAGE_SCENARIO_DEFAULT_PARAMS[SCENARIO_TYPES.REFINANCE].amortization,
                ),
            };
        case SCENARIO_TYPES.HELOC:
            return {
                ...params,
            };
    }
}

function formatRates(rates, scenario) {
    if (rates.length === 0) {
        return [];
    }

    if (scenario === SCENARIO_TYPES.HELOC) {
        // HELOC returns one rate per row so
        //  we don't have to format them to have both a fixed and variable
        //  rate per row, but we want them in the same shape for the component
        return rates.map(rate => ({
            term: 0, // HELOC returns term 0
            ...(rate.type === MORTGAGE_TYPES.VARIABLE
                ? {
                    [MORTGAGE_TYPES.VARIABLE]: {
                        ...rate,
                    },
                }
                : {
                    [MORTGAGE_TYPES.FIXED]: {
                        ...rate,
                        primeRate: undefined,
                    },
                }),
        }));
    }

    const initialArray = MORTGAGE_TERMS.map((item) => ({
        term: item,
    }));

    return rates.reduce((formattedRates, currentRate) => {
        const currentIndex = formattedRates.findIndex(rate => rate.term === currentRate.term);

        formattedRates[currentIndex] = {
            ...formattedRates[currentIndex],
            [currentRate.type]: {
                ...currentRate,
                // Only want prime rate on variable rates
                primeRate: currentRate.type === MORTGAGE_TYPES.VARIABLE ? currentRate.primeRate : undefined,
            },
        };

        return formattedRates;
    }, initialArray);
}

export default fetchProductTableBestMortgageRates;
